import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const deliverables = [{
  title: 'A clear one-page brand summary',
  body: 'Who the brand is for, what it stands for, key messages, and positioning in one simple document.'
}, {
  title: 'Audience and positioning overview',
  body: 'Short summary of your primary audience and how the brand should sit in the market.'
}, {
  title: 'Refined brand story and key messages',
  body: 'A concise brand story and 4–6 core messages you can use on your website, social and conversations.'
}, {
  title: 'Naming support and guidance',
  body: 'Simple naming criteria, a shortlist of directions, and guidance to help you choose and stress‑test a final name.'
}, {
  title: 'Logo suite',
  body: '1 primary logo · 1 secondary or stacked version · 1 small mark / icon (for social or clasp use).'
}, {
  title: 'Colour palette and typography',
  body: 'Primary and secondary colours, recommended typefaces, and basic usage guidance.'
}, {
  title: 'Brand assets for launch',
  body: 'Email signature (1) · LinkedIn banners (2: personal + company) · Social post templates (2–3) · Email newsletter header / layout (1) · One print item: roll-up banner or A5/A4 flyer (up to 2 pages). Designs supplied, not printing.'
}, {
  title: 'File exports ready for use',
  body: 'Logo files in PNG, SVG and JPEG · Social templates as editable files (Figma / Canva / Adobe, depending on setup) · Print item supplied as print-ready PDF.'
}, {
  title: 'Short, practical brand guidelines (PDF)',
  body: 'Logo usage, colour, typography, tone of voice highlights, and examples of how to apply the brand.'
}, {
  title: 'Organised handover',
  body: 'All final files supplied in clearly labelled folders, ready for web, digital and print use.'
}];
export function WhatsIncluded() {
  const [hovered, setHovered] = useState<number | null>(null);
  return <section id="included" className="py-20 md:py-32 px-5 md:px-10 bg-paper border-b border-rule/60" data-id="element-360">
      <div className="max-w-6xl mx-auto" data-id="element-361">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 mb-12 md:mb-16" data-id="element-362">
          <div className="lg:col-span-5" data-id="element-363">
            <ScrollReveal data-id="element-364">
              <p className="text-[11px] uppercase tracking-[0.3em] text-red font-bold mb-6" data-id="element-365">
                — What's Included
              </p>
              <h2 className="text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.04em] text-ink font-light mb-8" data-id="element-366">
                Final
                <br data-id="element-367" />
                <span className="font-bold italic" data-id="element-368">deliverables.</span>
              </h2>
              <p className="text-base md:text-lg text-ink-soft leading-relaxed font-light max-w-md mb-6" data-id="element-369">
                A focused brand foundation for businesses that need strategic
                clarity and a credible visual identity before launch.
              </p>
              <p className="text-sm text-ink-mute italic font-light" data-id="element-370">
                By the end of this project, you will leave with —
              </p>
            </ScrollReveal>
          </div>

          <div className="lg:col-span-7 lg:pt-32" data-id="element-371">
            <ScrollReveal delay={0.1} data-id="element-372">
              <div className="bg-ink text-ivory p-6 md:p-8 rounded-sm" data-id="element-373">
                <p className="text-[10px] uppercase tracking-[0.25em] text-red mb-3 font-bold" data-id="element-374">
                  10 deliverables · 1 brand foundation
                </p>
                <p className="text-lg md:text-xl text-ivory leading-relaxed font-light" data-id="element-375">
                  Everything you need to launch with confidence — strategy,
                  story, identity, assets and guidelines, all clearly organised
                  for handover.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>

        {/* Numbered deliverables list */}
        <div className="border-t border-ink" data-id="element-376">
          {deliverables.map((d, i) => <ScrollReveal key={i} delay={i * 0.04} data-id="element-377">
              <motion.div className="group relative border-b border-rule/70 cursor-default" onHoverStart={() => setHovered(i)} onHoverEnd={() => setHovered(null)} data-id="element-378">
                <div className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8 items-baseline relative" data-id="element-379">
                  {/* Hover red bar */}
                  <AnimatePresence data-id="element-380">
                    {hovered === i && <motion.div layoutId="hover-bar" className="absolute left-0 top-0 bottom-0 w-[3px] bg-red" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} data-id="element-381" />}
                  </AnimatePresence>

                  <div className="col-span-2 md:col-span-1 pl-2 md:pl-4" data-id="element-382">
                    <motion.span className="text-2xl md:text-4xl font-light text-ink-mute tracking-tight block" animate={{
                  color: hovered === i ? '#E84B1A' : '#6B665C',
                  x: hovered === i ? 4 : 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-383">
                      {String(i + 1).padStart(2, '0')}
                    </motion.span>
                  </div>

                  <div className="col-span-10 md:col-span-7" data-id="element-384">
                    <h3 className="text-lg md:text-2xl text-ink font-medium tracking-tight leading-tight mb-2" data-id="element-385">
                      {d.title}
                    </h3>
                    <p className="text-sm md:text-base text-ink-soft font-light leading-relaxed" data-id="element-386">
                      {d.body}
                    </p>
                  </div>

                  <div className="hidden md:flex md:col-span-4 justify-end items-center" data-id="element-387">
                    <motion.div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-ink-mute font-bold" animate={{
                  x: hovered === i ? 0 : 8,
                  opacity: hovered === i ? 1 : 0.3
                }} transition={{
                  duration: 0.3
                }} data-id="element-388">
                      Included
                      <ArrowUpRightIcon className="w-3 h-3" data-id="element-389" />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>)}
        </div>
      </div>
    </section>;
}